import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { fetchNotificationSettings } from "../../store/remediation/remediationSlice";
import RemediationSnackbarProvider from "./components/common/SnackbarHost";
import { useIsRemediationAdmin } from "./components/common/hooks";
import RequestsList from "./components/RequestsList";
import IamStackPanel from "./components/IamStack";
import NotificationSettingsPanel from "./components/NotificationSettings";

const TAB_REQUESTS = "requests";
const TAB_IAM = "iam";
const TAB_NOTIF = "notifications";

/**
 * Remediation Hub — SecOps menu page.
 *
 * Internal sub-tabs:
 *   - Requests (default landing) — urgency-sorted list, polling, bulk actions
 *   - IAM Stack — CFN template viewer with copy/download
 *   - Notification Settings — admin only
 *
 * Above the sub-tabs we surface a banner when the tenant is paused
 * (read-only — operators flip it via SQL per the integration guide).
 */
const RemediationHub = () => {
  const dispatch = useDispatch();
  const isAdmin = useIsRemediationAdmin();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return TAB_REQUESTS;
    return window.location.hash?.replace("#", "") || TAB_REQUESTS;
  });

  // Tenant settings drive both the "paused" banner above the sub-tabs and the
  // schedule validation in the Request Fix form / Re-schedule modal. Fetch once.
  const notifSettings = useSelector((s) => s.remediation?.notificationSettings);
  useEffect(() => {
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  useEffect(() => {
    document.title = "Remediation Hub";
  }, []);

  const handleTabChange = (_e, val) => {
    setActiveTab(val);
    try {
      window.history.replaceState(null, "", `#${val}`);
    } catch (_e) {
      /* no-op */
    }
  };

  const isPaused = Boolean(notifSettings?.data?.is_paused);

  // Tabs available to the user — Notification Settings is admin-only.
  const tabs = useMemo(() => {
    const items = [
      { value: TAB_REQUESTS, label: "Requests" },
      { value: TAB_IAM, label: "IAM Stack" },
    ];
    if (isAdmin) items.push({ value: TAB_NOTIF, label: "Notification Settings" });
    return items;
  }, [isAdmin]);

  // If a non-admin lands on the Notifications tab via hash, fall back to Requests.
  useEffect(() => {
    if (activeTab === TAB_NOTIF && !isAdmin) {
      setActiveTab(TAB_REQUESTS);
    }
  }, [activeTab, isAdmin]);

  return (
    <RemediationSnackbarProvider>
      <Box sx={{ pt: 2, px: { xs: 1, sm: 2 }, pb: 5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Remediation Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            File one-click fixes for security findings, schedule and approve them, and watch them
            execute against your AWS accounts.
          </Typography>
        </Box>

        {isPaused && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2 }}
          >
            Remediation is paused for this tenant. New requests can still be drafted, but the worker
            will not pick them up. Contact support if you need this lifted.
          </Alert>
        )}

        {notifSettings?.status === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: "text.secondary" }}>
            <CircularProgress size={14} /> Loading tenant settings…
          </Box>
        )}

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14 },
          }}
        >
          {tabs.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>

        {activeTab === TAB_REQUESTS && <RequestsList />}
        {activeTab === TAB_IAM && <IamStackPanel />}
        {activeTab === TAB_NOTIF && isAdmin && <NotificationSettingsPanel />}
      </Box>
    </RemediationSnackbarProvider>
  );
};

export default RemediationHub;
